import {
  getEqlWhereString,
  trackDashboardGlobalFilterEvents,
} from '@modules/dashboard/helpers';
import { DashboardActionType } from '@modules/dashboard/store/dashboardActionType';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { QueryBuilder } from '@modules/queryBuilder/components/QueryBuilder';
import { trackQBEvent } from '@modules/queryBuilder/events';
import { QueryOnChangeProps } from '@modules/queryBuilder/types';
import { Box } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { useGetEQLExpression } from 'v2/core/repository/edge-server/eql/useGetEQLExpression';
import useGetSourceDetailsMap from 'v2/core/repository/edge-server/source-details';
import { mapToEQL } from 'v2/core/repository/eql/mappers';
import { ExpressionType } from 'v2/core/repository/eql/types';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { useGetUser } from 'v2/core/services/user';
import { shallow } from 'zustand/shallow';

interface DashboardQueryBuilderV2Props {
  shouldSkipRender: boolean;
  areGlobalFiltersApplied: boolean;
  shouldAutoFocus: boolean;
}

export function DashboardQueryBuilderV2({
  shouldSkipRender,
  areGlobalFiltersApplied,
  shouldAutoFocus,
}: DashboardQueryBuilderV2Props) {
  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const { data: sourceDetailsMap } = useGetSourceDetailsMap();

  const { dispatch, whereClause } = useDashboardStore(
    ({ dispatch, query: { whereClause } }) => ({
      dispatch,
      whereClause,
    }),
    shallow,
  );

  const { eql } = useGetEQLExpression({
    tenantID,
    eqlProps: {
      whereClause,
      expressionType: ExpressionType.Records,
    },
  });

  const onExecute = ({
    whereClause: updatedWhereClause,
  }: QueryOnChangeProps) => {
    dispatch({
      type: DashboardActionType.SET_QUERY,
      payload: {
        whereClause: updatedWhereClause,
      },
    });

    dispatch({
      type: DashboardActionType.SET_VISUAL,
      payload: {
        queryBuilderKey: uuidv4(),
        shouldShowQueryBuilder: true,
      },
    });

    trackDashboardGlobalFilterEvents({
      eventName: 'Dashboard Global Filter - WhereClause Changed',
      query: {
        whereClause: getEqlWhereString(eql),
      },
      route: window.location.pathname,
    });

    trackQBEvent({
      eventName: 'Query Builder Action Event',
      action: {
        type: 'EXECUTE_QUERY',
        eql: mapToEQL({
          whereClause: updatedWhereClause ?? EmptyWhereClause,
          expressionType: ExpressionType.Records,
          sourceDetailsMap: sourceDetailsMap ?? new Map(),
        }),
        display: '',
        searchMetadata: '',
        qbContext: 'Dashboard',
      },
    });
  };

  return (
    <Box sx={{ mt: 2 }} data-testid="dashboard-query-builder">
      <QueryBuilder
        whereClause={whereClause}
        onExecute={onExecute}
        shouldSkipRender={shouldSkipRender}
        autoFocus={shouldAutoFocus}
        openOnFocus={false}
        buttonColor={areGlobalFiltersApplied ? 'info' : 'primary'}
        containerSxProps={
          areGlobalFiltersApplied
            ? {
                bgcolor: (theme) =>
                  theme.palette.misc.queryBuilder.dashboardFilter.bgColor,
              }
            : undefined
        }
      />
    </Box>
  );
}
